import { type ChangeEvent, useState, type ReactNode } from 'react';
import { InputAdornment, Stack, type StackProps } from '@mui/material';
import { ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { MHMTextField } from '../MHM/MHMTextField';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useRecoilState, useRecoilValue } from 'recoil';
import { CheckPermission } from '../CheckPermission';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { toSeconds, unitConverterMap } from '../../../utils/helper_functions';
import { companyDeviceSettings } from '../../../models/interfaces/company_interfaces';
import { changeCompanyDeviceAtom } from '../../../models/atoms/company_atoms';

type Props = StackProps & {
  title?: ReactNode;
  messageTimeKey: keyof companyDeviceSettings;
  messageTimeUnit: 'hours' | 'days';
  messageTimeError: string;
  measurementTimeKey: keyof companyDeviceSettings;
  measurementTimeUnit: 'minutes' | 'hours';
  measurementTimeError: string;
  totalMeasurementsError: string;
};

export function FrequenciesSettings(props: Props) {
  const {
    title,
    messageTimeKey,
    messageTimeUnit,
    messageTimeError,
    measurementTimeKey,
    measurementTimeUnit,
    measurementTimeError,
    totalMeasurementsError,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [times, setTimes] = useRecoilState(changeCompanyDeviceAtom);
  const [messageTimeInputError, setMessageTimeInputError] = useState('');
  const [measurementTimeInputError, setMeasurementTimeInputError] =
    useState('');
  const canManageDevices = useRecoilValue(canManageDevicesSelector);

  const unitDisplayMap = {
    hours: t('Hours'),
    days: t('Days'),
    minutes: t('Minutes'),
  } as const;

  const displayingMessageTimeError =
    t(messageTimeInputError) || messageTimeError || '';
  const isMessageTimeError = Boolean(
    displayingMessageTimeError || totalMeasurementsError,
  );
  const displayingMeasurementTimeError =
    t(measurementTimeInputError) || measurementTimeError;
  const isMeasurementTimeError = Boolean(
    displayingMeasurementTimeError || totalMeasurementsError,
  );

  function handleMessageWaitTimeChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    if (!/^[0-9.,]+$/.test(value)) {
      setMessageTimeInputError('Please provide a valid number!');
      return;
    }

    setMessageTimeInputError('');
    const parsedValue = toSeconds(Number(value), messageTimeUnit);
    setTimes(prev =>
      prev
        ? { ...prev, [messageTimeKey]: parsedValue, change_time: true }
        : prev,
    );
  }

  function handleMeasurementSleepTimeChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    if (!/^[0-9.,]+$/.test(value)) {
      setMeasurementTimeInputError('Please provide a valid number!');
      return;
    }

    setMeasurementTimeInputError('');
    const parsedValue = toSeconds(Number(value), measurementTimeUnit);
    setTimes(prev =>
      prev
        ? { ...prev, [measurementTimeKey]: parsedValue, change_time: true }
        : prev,
    );
  }

  if (!times) {
    return null;
  }

  return (
    <Stack gap={1.5} {...rest}>
      {title ? (
        <MHMTypography variant="h6" color="primary">
          {title}
        </MHMTypography>
      ) : null}

      <CheckPermission hasPermission={canManageDevices}>
        <MHMTextField
          label={t('Relay Frequency')}
          defaultValue={unitConverterMap[messageTimeUnit](
            times[messageTimeKey],
          )}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {unitDisplayMap[messageTimeUnit]}
                </InputAdornment>
              ),
            },
          }}
          error={isMessageTimeError}
          helperText={
            <ErrorText component="span">{displayingMessageTimeError}</ErrorText>
          }
          size="small"
          fullWidth
          disabled={!canManageDevices}
          onChange={handleMessageWaitTimeChange}
        />
      </CheckPermission>

      <CheckPermission hasPermission={canManageDevices}>
        <MHMTextField
          label={t('Sensor Reading Frequency')}
          defaultValue={unitConverterMap[measurementTimeUnit](
            times[measurementTimeKey],
          )}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {unitDisplayMap[measurementTimeUnit]}
                </InputAdornment>
              ),
            },
          }}
          error={isMeasurementTimeError}
          helperText={
            <>
              {displayingMeasurementTimeError ? (
                <ErrorText component="span">
                  {displayingMeasurementTimeError}
                </ErrorText>
              ) : null}
              {totalMeasurementsError ? (
                <ErrorText component="span">{totalMeasurementsError}</ErrorText>
              ) : null}
            </>
          }
          size="small"
          fullWidth
          disabled={!canManageDevices}
          onChange={handleMeasurementSleepTimeChange}
        />
      </CheckPermission>
    </Stack>
  );
}
export { unitConverterMap };
